import React from 'react';
import PropTypes from 'prop-types';
import { Config, I18nContext, formatPrice } from '@deporvillage/utils-library-component';
import { initialize as initializeTracking, track } from '@deporvillage/tracking-library-component';
import Autocomplete, { AutocompleteGridClick } from '@deporvillage/autocomplete-component';
import { CartDisplayClick } from '@deporvillage/cart-display-component';
import { CountriesListDropDown } from '@deporvillage/countries-list-component';
import { MyAccountEvents } from '@deporvillage/my-account-component';
import HeaderActions from '@deporvillage/header-actions-component';
import NavigationBar from '@deporvillage/navigation-bar-component';
import HeaderLogo from './HeaderLogo';

import styles from './HeaderDesktop.module.scss';

const PageType = {
    Home: 'home',
};


const HeaderCategoryLevel = {
    Category: 0,
    Subcategory: 1,
    Family: 2,
};

const HeaderCategoryClick = {
    Category: 'category',
    Subcategory: 'subcategory',
    Family: 'family',
};

const getHeaderCategories = (categories) => (
    categories.filter((i) => {
        if (i.desktop && i.items) i.items = getHeaderCategories(i.items);
        return i.desktop;
    })
);

const clone = (c) =>
    c.reduce((p, v) => {
        p.push({ ...v, ...(v.items ? { items: clone(v.items) } : {}) });
        return p;
    }, []);

const HeaderDesktop = ({ model }) => {

    const { translate } = React.useContext(I18nContext);
    const { lang, page, freeDeliveryThreshold, store, country, tracking, debug } = React.useContext(Config);
    const [user, setUser] = React.useState({});
    const isFreeDelivery = freeDeliveryThreshold && store.code === country.code;

    const { nav, showFinance, showBestPrice, items, amount, totalItems, isOpened } = model;

    const headerCategories = React.useMemo(() => {
        return getHeaderCategories(clone(nav));
    }, [nav]);

    const handleUserData = ({ detail }) => {
        setUser({
            userName: detail.name,
            isLogged: detail.logged_in,
            sessionId: detail.session_id,
            favorites: detail.favorites,
        });

        /* eslint-disable-next-line no-undef */
        window.removeEventListener('user:data', handleUserData);
    };

    const handleWishListUpdate = React.useCallback((obj) => {
        const favorites = (user.favorites || 0) + (obj.detail.isSaved ? 1 : -1);
        setUser({ ...user, favorites: favorites < 0 ? 0 : favorites });
    }, [user]);

    const handleCategoryClick = (url, level) => {

        switch (level) {
            case HeaderCategoryLevel.Subcategory:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Subcategoria Menu header',
                });
                break;
            case HeaderCategoryLevel.Family:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Familia Menu header',
                });
                break;
            case HeaderCategoryLevel.Category:
            default:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Menu Header',
                    label: 'Click en link Categoria Menu header',
                });
                break;
        }

        /* eslint-disable-next-line no-undef */
        if (url) window.location.href = url;
    };

    /* istanbul ignore next */
    const handleOnSubmitAutocomplete = () => track({
        type: 'ACTION',
        name: 'GENERIC_CLICK',
        action: 'Ir a Busqueda',
        label: 'Click en lanzar Busqueda'
    });

    /* istanbul ignore next */

    /* istanbul ignore next */
    const handleOnClickAutocomplete = (e) => {

        switch (e) {
            case AutocompleteGridClick.Brand:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en marca',
                });
                break;
            case AutocompleteGridClick.Category:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en categoria',
                });
                break;
            case AutocompleteGridClick.All:
                track({
                type: 'ACTION',
                name: 'GENERIC_CLICK',
                action: 'Ir a Busqueda',
                label: 'Click en ver todo',
                });
                break;
            case AutocompleteGridClick.Close:
                track({
                type: 'ACTION',
                name: 'GENERIC_CLICK',
                action: 'Ir a Busqueda',
                label: 'Click en cruz de cierre',
                });
                break;
            case AutocompleteGridClick.Item:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Busqueda',
                    label: 'Click en listado Busqueda'
                });
                break;
        }
    };

    /* istanbul ignore next */
    const handleOnClickHeaderActions = (e) => {
        /* istanbul ignore next */
        switch (e) {
            case CartDisplayClick.Item:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en link Producto carrito',
                });
                break;
            case CartDisplayClick.Icon:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en Icono carrito',
                });
                break;
            case CartDisplayClick.ViewCart:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en boton Ver carrito',
                });
                /* eslint-disable-next-line no-undef */
                window.location.href = '/checkout/cart';
                break;
            case CartDisplayClick.PlaceOrder:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Carrito',
                    label: 'Click en boton Realizar pedido',
                });
                /* eslint-disable-next-line no-undef */
                window.location.href = '/onestepcheckout';
                break;
            case MyAccountEvents.InputEmail:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Rellenar datos personales',
                    label: 'Login - Email',
                });
                break;
            case MyAccountEvents.InputPassword:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Rellenar datos personales',
                    label: 'Login - Contraseña',
                });
                break;
            case MyAccountEvents.ButtonLogin:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Iniciar sesión',
                    label: 'Login - Click en el botón',
                });
                break;
            case MyAccountEvents.LinkSignUp:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Regístrate',
                    label: 'Login - Click en el botón',
                });
                break;
            case MyAccountEvents.LinkForgotPassword:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: '¿Olvidaste tu contraseña?',
                    label: 'Login - Click en el enlace ¿Olvidaste tu contraseña?',
                });
                break;
            case MyAccountEvents.GoogleSocialButtonLogin:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Iniciar sesión',
                    label: 'Click en botón Social Login',
                });
                break;
        }
    };

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('wishlist:update', handleWishListUpdate);
        return () => { window.removeEventListener('wishlist:update', handleWishListUpdate); };
        /* eslint-enable no-undef */
    }, [handleWishListUpdate]);

    React.useEffect(() => {

        initializeTracking({ ...tracking, page, debug });

        /* eslint-disable no-undef */
        window.addEventListener('user:data', handleUserData);
        window.dispatchEvent(new CustomEvent('user:getdata'));
        return () => { window.removeEventListener('user:data', handleUserData); };
        /* eslint-enable no-undef */
    }, []);

    return (
        <div className={ styles['header-desktop-component'] }>

            <div className={ styles['header-services'] }>
                <div className={ styles['header-services-wrapper'] }>

                    <a href={ translate('header.services.shipping_link') } rel="nofollow">
                    <span className={ `${ styles['icon']} icon icon-transport`}/>
                        <span dangerouslySetInnerHTML={ {
                            __html: isFreeDelivery ? translate('header.services.shipping_free', {
                                shipping_cost: formatPrice(
                                    freeDeliveryThreshold, country.currency_code, lang, {
                                        decimalDigits: 0,
                                        decimalPadding: 0
                                    })
                            }) : translate('header.services.shipping_express')
                        } }/>
                    </a>

                    <a href={ translate('header.services.delivery_link') } rel="nofollow">
                    <span className={ `${ styles['icon']} icon icon-schedule`}/>
                        <span dangerouslySetInnerHTML={ {
                            __html: translate(isFreeDelivery ? 'header.services.express_delivery' : 'header.services.delivery')
                        } }/>
                    </a>

                    <a href={ translate('header.services.return_link') } rel="nofollow">
                    <span className={ `${ styles['icon']} icon icon-refresh`}/>
                        <span dangerouslySetInnerHTML={ { __html: translate('header.services.return') } }/>
                    </a>

                    { !showFinance ? null : <a href={ translate('header.services.finance_link') } rel="nofollow">
                    <span className={ `${ styles['icon']} icon icon-credit-card`}/>
                        <span dangerouslySetInnerHTML={ { __html: translate('header.services.finance') } }/>
                    </a> }

                    { !showBestPrice ? null : <a href={ translate('header.services.price_link') } rel="nofollow">
                        <span className={ `${ styles['icon']} icon icon-tag`} />
                        <span dangerouslySetInnerHTML={ { __html: translate('header.services.price') } }/>
                    </a> }

                    <div className={ styles['countries-list-wrapper'] }>
                        <CountriesListDropDown/>
                    </div>

                </div>
            </div>

            <header>

                <div className={ styles['header-logo'] }>
                    <HeaderLogo size={ { width: 182, height: 86 } }
                                wrapper={ (children) => PageType.Home === page ? <h1>{ children }</h1> : children }/>
                </div>

                <div className={ styles['header-autocomplete'] }>
                    <Autocomplete
                        onSubmit={ handleOnSubmitAutocomplete }
                        onClick={ handleOnClickAutocomplete }/>
                </div>

                <div className={ styles['header-actions'] }>
                    <HeaderActions
                        user={ user }
                        onClick={ handleOnClickHeaderActions }
                        onChange={ handleOnClickHeaderActions }
                        items={ items }
                        amount={ amount }
                        totalItems={ totalItems }
                        isOpened= { isOpened }
                    />
                </div>

                <nav className={ styles['header-categories'] }>
                    <NavigationBar items={ headerCategories } onClick={ handleCategoryClick }/>
                </nav>

            </header>
        </div>
    );
};

HeaderDesktop.propTypes = {
    model: PropTypes.object,
    onClick: PropTypes.func,
};

export default HeaderDesktop;
export { HeaderCategoryClick };
